import { supabase } from '../lib/supabase';

/**
 * Upload image to Supabase Storage with enhanced error handling and synchronization
 * @param {File} file - The image file to upload
 * @param {string} bucket - The storage bucket name
 * @param {string} filePath - The file path within the bucket
 * @returns {Promise<{data?: {path: string, publicUrl?: string}, error?: string}>}
 */
export const uploadImage = async (file, bucket, filePath) => {
  try {
    // Enhanced validation
    if (!file) {
      return { error: 'No file provided' };
    }

    // More comprehensive MIME type checking
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    const isValidImage = file?.type && (
      file?.type?.startsWith('image/') || 
      allowedTypes?.includes(file?.type?.toLowerCase())
    );

    if (!isValidImage) {
      return { error: 'File must be an image (JPG, JPEG, PNG, GIF, WebP, SVG)' };
    }

    // Check file size (10MB max for most buckets, 5MB for profile images)
    const maxSize = bucket === 'artist-profile-images' ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file?.size > maxSize) {
      const maxSizeMB = maxSize / (1024 * 1024);
      return { error: `File size must be less than ${maxSizeMB}MB` };
    }

    // Remove any existing file at the same path to prevent conflicts
    try {
      await supabase?.storage?.from(bucket)?.remove([filePath]);
    } catch (removeError) {
      // Ignore removal errors - file might not exist
      console.log('File removal info (expected if new upload):', removeError?.message);
    }

    // Upload file to Supabase Storage with retry mechanism
    let uploadResult;
    let attempts = 0;
    const maxAttempts = 3;

    while (attempts < maxAttempts) {
      attempts++;
      
      try {
        uploadResult = await supabase?.storage?.from(bucket)?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

        if (uploadResult?.error) {
          if (attempts === maxAttempts) {
            console.error(`Upload failed after ${maxAttempts} attempts:`, uploadResult?.error);
            return { error: `Upload failed: ${uploadResult?.error?.message}` };
          }
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
          continue;
        }

        // Success - break out of retry loop
        break;
      } catch (uploadError) {
        if (attempts === maxAttempts) {
          console.error('Upload error:', uploadError);
          return { error: 'Failed to upload image to storage' };
        }
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
      }
    }

    // Enhanced file verification with better logic and timing
    await new Promise(resolve => setTimeout(resolve, 1500)); // Wait for storage consistency

    try {
      // Try to get the file directly first
      const { data: fileInfo, error: infoError } = await supabase?.storage?.from(bucket)?.info(filePath);
      
      if (!infoError && fileInfo) {
        console.log(`File verification successful for ${filePath}`);
        return {
          data: {
            path: uploadResult?.data?.path || filePath,
            fullPath: filePath
          }
        };
      }

      // Fallback: Try listing files in the folder
      const folderPath = filePath?.substring(0, filePath?.lastIndexOf('/')) || '';
      const fileName = filePath?.split('/')?.pop();
      
      const { data: fileList, error: listError } = await supabase?.storage?.from(bucket)?.list(folderPath, {
          limit: 50,
          offset: 0
        });

      if (!listError && fileList) {
        let fileExists = fileList?.find(file => 
          file?.name === fileName ||
          file?.name?.includes(fileName?.split('_')?.slice(-1)?.[0]?.split('.')?.[0]) // Match base filename
        );
        
        if (fileExists) {
          console.log(`File verification successful via list for ${filePath}`);
          return {
            data: {
              path: uploadResult?.data?.path || filePath,
              fullPath: filePath
            }
          };
        }
      }

      // Final fallback: If verification fails but upload succeeded, trust the upload
      console.warn(`File verification inconclusive for ${filePath}, but upload succeeded - proceeding`);
      return {
        data: {
          path: uploadResult?.data?.path || filePath,
          fullPath: filePath
        }
      };

    } catch (verifyError) {
      // If verification fails entirely, still return success since upload worked
      console.warn('File verification failed, but upload completed successfully:', verifyError?.message);
      return {
        data: {
          path: uploadResult?.data?.path || filePath,
          fullPath: filePath
        }
      };
    }

  } catch (error) {
    console.error('Image upload error:', error);
    return { error: 'Failed to upload image' };
  }
};

/**
 * Enhanced signed URL generation with better error handling and graceful fallback
 * @param {string} bucket - The storage bucket name
 * @param {string} filePath - The file path within the bucket
 * @param {number} expirySeconds - URL expiry time in seconds (default: 1 hour)
 * @returns {Promise<{data?: {signedUrl: string}, error?: string}>}
 */
export const getSignedImageUrl = async (bucket, filePath, expirySeconds = 3600) => {
  try {
    if (!filePath) {
      return { error: 'No file path provided' };
    }

    // Try to generate signed URL directly first
    let urlResult;
    let attempts = 0;
    const maxAttempts = 3;

    while (attempts < maxAttempts) {
      attempts++;
      
      try {
        urlResult = await supabase?.storage?.from(bucket)?.createSignedUrl(filePath, expirySeconds);

        if (urlResult?.error) {
          // Some errors are temporary - retry
          if (urlResult?.error?.message?.includes('timeout') || 
              urlResult?.error?.message?.includes('network') ||
              attempts < maxAttempts) {
            console.warn(`Signed URL attempt ${attempts} failed:`, urlResult?.error?.message);
            await new Promise(resolve => setTimeout(resolve, 500 * attempts));
            continue;
          }
          
          // For "not found" errors, return specific error
          if (urlResult?.error?.message?.includes('not found') || 
              urlResult?.error?.message?.includes('does not exist')) {
            return { error: 'Image file not found in storage' };
          }
          
          return { error: `Failed to generate image URL: ${urlResult?.error?.message}` };
        }

        // Success
        break;
      } catch (urlError) {
        if (attempts === maxAttempts) {
          console.error('Signed URL generation error:', urlError);
          return { error: 'Failed to get image URL' };
        }
        await new Promise(resolve => setTimeout(resolve, 500 * attempts));
      }
    }

    // Validate the signed URL before returning
    if (!urlResult?.data?.signedUrl) {
      return { error: 'No signed URL generated' };
    }

    return urlResult;

  } catch (error) {
    console.error('Get signed URL error:', error);
    return { error: 'Failed to get image URL' };
  }
};

/**
 * Delete image from Supabase Storage
 * @param {string} bucket - The storage bucket name
 * @param {string} filePath - The file path within the bucket
 * @returns {Promise<{data?: any, error?: string}>}
 */
export const deleteImage = async (bucket, filePath) => {
  try {
    if (!filePath) {
      return { data: null };
    }

    const { data, error } = await supabase?.storage?.from(bucket)?.remove([filePath]);

    if (error) {
      console.error('Delete error:', error);
      return { error: error?.message };
    }

    return { data };
  } catch (error) {
    console.error('Image delete error:', error);
    return { error: 'Failed to delete image' };
  }
};

/**
 * Generate file path for user's image with enhanced uniqueness
 * @param {string} userId - The user's ID
 * @param {string} category - The image category (e.g., 'profile', 'promotion', 'work', 'shop')
 * @param {string} fileName - The original file name
 * @returns {string} - The generated file path
 */
export const generateImagePath = (userId, category, fileName) => {
  // Clean filename and add timestamp to avoid conflicts
  const timestamp = Date.now();
  const randomSuffix = Math.random()?.toString(36)?.substring(2, 8); // Add randomness
  const cleanFileName = fileName?.replace(/[^a-zA-Z0-9.-]/g, '_');
  const fileExtension = cleanFileName?.split('.')?.pop();
  const baseName = cleanFileName?.replace(`.${fileExtension}`, '')?.substring(0, 20); // Limit base name length
  
  return `${userId}/${category}/${timestamp}_${randomSuffix}_${baseName}.${fileExtension}`;
};

/**
 * Get bucket name for image category
 * @param {string} category - The image category
 * @returns {string} - The bucket name
 */
export const getBucketForCategory = (category) => {
  const bucketMap = {
    profile: 'artist-profile-images',
    promotion: 'artist-promotion-assets', 
    work: 'artist-work-images',
    shop: 'artist-shop-images'
  };
  
  return bucketMap?.[category] || 'artist-profile-images';
};

// Enhanced cache invalidation helper
export const invalidateImageCache = (artistId) => {
  if (typeof window !== 'undefined' && window.localStorage) {
    // Clear all image-related cache for the artist
    const cacheKeys = [
      `artist_data_${artistId}`,
      `promotion_${artistId}`,
      `signed_urls_${artistId}`,
      `image_cache_${artistId}`
    ];
    
    cacheKeys?.forEach(key => {
      window.localStorage?.removeItem(key);
    });
    
    // Also clear any cached signed URLs that may be expired
    Object.keys(window.localStorage)?.forEach(key => {
      if (key?.startsWith(`signed_url_${artistId}_`)) {
        window.localStorage?.removeItem(key);
      }
    });
  }
};

/**
 * Enhanced upload and get URL for image in one call with improved synchronization and database validation
 * @param {File} file - The image file to upload
 * @param {string} userId - The user's ID
 * @param {string} category - The image category
 * @returns {Promise<{data?: {url: string, path: string}, error?: string}>}
 */
export const uploadImageAndGetUrl = async (file, userId, category) => {
  try {
    if (!file) {
      return { error: 'No file provided' };
    }

    // Enhanced file validation
    const allowedImageTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml'
    ];

    const isValidImageType = file?.type && (
      file?.type?.startsWith('image/') || 
      allowedImageTypes?.includes(file?.type?.toLowerCase())
    );

    if (!isValidImageType) {
      console.error('Invalid file type:', file?.type);
      return { error: 'File must be an image (JPG, PNG, GIF, WebP, SVG)' };
    }

    const bucket = getBucketForCategory(category);
    const filePath = generateImagePath(userId, category, file?.name);
    
    // Step 1: Upload to storage with enhanced verification
    console.log(`Uploading ${file?.name} to ${bucket}/${filePath}`);
    let uploadResult = await uploadImage(file, bucket, filePath);
    
    if (uploadResult?.error) {
      return { error: uploadResult?.error };
    }

    // Step 2: Enhanced verification and URL generation with longer wait times
    console.log(`Verifying upload and generating signed URL for ${uploadResult?.data?.path}`);
    
    // Wait longer for Supabase storage consistency
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Verify file exists before generating URL
    let verificationAttempts = 0;
    const maxVerificationAttempts = 5;
    let fileExists = false;

    while (verificationAttempts < maxVerificationAttempts && !fileExists) {
      verificationAttempts++;
      
      try {
        // Check if file exists by trying to get its info
        const { data: fileInfo, error: infoError } = await supabase?.storage?.from(bucket)?.info(uploadResult?.data?.path);
        
        if (!infoError && fileInfo) {
          fileExists = true;
          console.log(`File verification successful on attempt ${verificationAttempts}`);
          break;
        }
        
        // Alternative verification: Try listing files
        const folderPath = uploadResult?.data?.path?.substring(0, uploadResult?.data?.path?.lastIndexOf('/')) || '';
        const fileName = uploadResult?.data?.path?.split('/')?.pop();
        
        const { data: fileList, error: listError } = await supabase?.storage?.from(bucket)?.list(folderPath, {
          limit: 100,
          offset: 0
        });

        if (!listError && fileList) {
          const foundFile = fileList?.find(file => 
            file?.name === fileName ||
            file?.name?.includes(fileName?.split('_')?.slice(-1)?.[0]?.split('.')?.[0])
          );
          
          if (foundFile) {
            fileExists = true;
            console.log(`File verification successful via list on attempt ${verificationAttempts}`);
            break;
          }
        }
        
        // Wait before retry
        if (verificationAttempts < maxVerificationAttempts) {
          console.log(`File verification attempt ${verificationAttempts} failed, retrying...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * verificationAttempts));
        }
        
      } catch (verifyError) {
        console.warn(`File verification attempt ${verificationAttempts} error:`, verifyError?.message);
        if (verificationAttempts < maxVerificationAttempts) {
          await new Promise(resolve => setTimeout(resolve, 1000 * verificationAttempts));
        }
      }
    }

    // Generate signed URL with enhanced retry logic
    let urlResult = await getSignedImageUrl(bucket, uploadResult?.data?.path, 7200);
    
    // If URL generation fails, try with extended delays
    if (urlResult?.error) {
      console.log('Initial URL generation failed, trying with extended retries...');
      
      for (let retry = 1; retry <= 5; retry++) {
        console.log(`Extended URL generation retry ${retry}...`);
        
        // Wait progressively longer
        await new Promise(resolve => setTimeout(resolve, 1500 * retry));
        
        urlResult = await getSignedImageUrl(bucket, uploadResult?.data?.path, 7200);
        if (!urlResult?.error) {
          console.log(`URL generation successful on extended retry ${retry}`);
          break;
        }
      }
      
      // If still failing, but file was uploaded and verified, provide fallback
      if (urlResult?.error && fileExists) {
        console.warn('URL generation failed but file exists, providing temporary solution');
        // Clear caches to ensure fresh load on next page visit
        invalidateImageCache(userId);
        
        return { 
          data: {
            path: uploadResult?.data?.path,
            url: null // Will be regenerated on page reload
          },
          error: null
        };
      }
      
      // If both file verification and URL generation failed
      if (urlResult?.error && !fileExists) {
        return { 
          error: `Upload may have failed. File verification: ${fileExists ? 'SUCCESS' : 'FAILED'}, URL generation: FAILED - ${urlResult?.error}` 
        };
      }
    }
    
    // Step 3: Clear related caches for fresh data
    invalidateImageCache(userId);
    
    console.log(`Successfully uploaded, verified, and generated URL for ${file?.name}`);
    return {
      data: {
        path: uploadResult?.data?.path,
        url: urlResult?.data?.signedUrl
      },
      error: null
    };
  } catch (error) {
    console.error('Upload and URL generation error:', error);
    return { error: 'Failed to upload and generate URL' };
  }
};

/**
 * Batch process multiple image URLs with proper error handling
 * @param {string} bucket - The bucket name
 * @param {Array} imagePaths - Array of image paths
 * @param {number} expirySeconds - URL expiry time
 * @returns {Promise<Array>} Array of objects with path and signedUrl
 */
export const getMultipleSignedUrls = async (bucket, imagePaths, expirySeconds = 7200) => {
  if (!imagePaths || imagePaths?.length === 0) {
    return [];
  }

  const results = await Promise.all(
    imagePaths?.map(async (path) => {
      if (!path) return { path: null, signedUrl: null, error: 'No path provided' };

      try {
        const result = await getSignedImageUrl(bucket, path, expirySeconds);
        return {
          path,
          signedUrl: result?.data?.signedUrl || null,
          error: result?.error || null
        };
      } catch (error) {
        return {
          path,
          signedUrl: null,
          error: error?.message || 'Failed to generate signed URL'
        };
      }
    })
  );

  return results;
};